/*  MUI  */
import { Box, Paper } from '@mui/material';

/*  Shared  */
import { debug } from 'application/Shared/Utils';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AccountsInventoryReport } from '../AccountsInventory/AccountsInventoryReport';
import Adoption from '../Adoption/Adoption';
import { ApplicationMapReports } from '../ApplicationMapReports';
import { ApplicationOverlapReport } from '../ApplicationOverlap/ApplicationOverlapReport';
import { BrowserReport } from '../Browser/BrowserReport';
import BusinessOwnerReports from '../BusinessOwnerReports';
import CategoryReports from '../CategoryReports';
import { ContractsReport } from '../Contracts/ContractsReport';
import Discovery from '../Discovery/Discovery';
import LibraryReports from '../LibraryReports';
import MicrosoftOfficeOverlapReports from '../MicrosoftOfficeOverlapReports';
import Offboard from '../Offboard/Report';
import PartnerApplicationOverviewReports from '../PartnerApplicationOverviewReports';
import PartnerAppUsage from '../PartnerApps/Usage';
import PartnerUsers from '../PartnerUsers/Report';
import { QBRFirstReports } from '../QBR/QBR';
import { ReportSummary } from '../ReportSummary';
import ShadowITReports from '../ShadowITReports';
import { StatusReports } from '../StatusReports';
import { UsageReports } from '../Usage/Usage';
import UserApps from '../UserApps/UserApps';
import VendorIncident from '../VendorIncident/Containers/Report';
import { isV2Report } from '../helpers';

const getReportType = ({ renderingScheduledReport, reportSetup, tab }) => {
    if (tab) return tab;
    if (!renderingScheduledReport) return null;
    if (!reportSetup?.reports?.length) return null;

    const { reports } = reportSetup;
    const reportTypes = reports.map((r) => r.type);
    const uniqueReportTypes = reportTypes.filter(
        (rt, i) => reportTypes.indexOf(rt) === i
    );

    if (uniqueReportTypes > 1) {
        debug.e('Too many report types', uniqueReportTypes);
        return null;
    }

    return uniqueReportTypes[0];
};

const RenderCurrentReport = ({
    renderingScheduledReport,
    reportData,
    reportSetup,
    reportType,
    tab
}) => {
    const { t } = useTranslation();

    const sharedProps = {
        renderingScheduledReport,
        reportData: reportData[reportType],
        reportSetup
    };

    switch (reportType) {
        case 'AccountsInventory':
            return <AccountsInventoryReport {...sharedProps} />;
        case 'ApplicationOverlap':
            return <ApplicationOverlapReport {...sharedProps} />;

        // Partner Reports - Application Overview - Applications Used Tab
        // Partner Reports - Application Overview - Application Usage Tab
        case 'ApplicationsUsed':
        case 'ApplicationUsage':
            return (
                <Box p="16px">
                    <PartnerApplicationOverviewReports
                        {...sharedProps}
                        tab={tab}
                    />
                </Box>
            );

        // Partner Report - Vendor Incident Report
        case 'ClientImpact':
            return <VendorIncident {...sharedProps} />;

        // Partner User Inventory Report
        case 'PartnerUsers':
            return <PartnerUsers {...sharedProps} />;

        //  Partner Application Usage Report, currently hidden
        case 'TopApplicationsUsed':
            return <PartnerAppUsage {...sharedProps} />;

        // Admin Discovery Outcome Report
        case 'Discovery':
            return <Discovery {...sharedProps} />;

        // Admin Report - QBR Report
        case 'QBRFirst':
            return <QBRFirstReports {...sharedProps} />;

        // Admin Offboard Report and Workflow
        case 'UserOffboard':
            return <Offboard {...sharedProps} />;

        // Admin Report - Browser Account Overview
        case 'Browser':
            return <BrowserReport {...sharedProps} />;

        // Admin Report - Organization Usage Report - Usage Reports Tab
        case 'Usage':
            return <UsageReports {...sharedProps} />;
        // Admin Report - Organization Usage Report - Library Report Tab
        case 'Library':
            return (
                <Box p="16px">
                    <ReportSummary
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        title={t('Reports.report_library')}
                    />
                    <LibraryReports {...sharedProps} />
                </Box>
            );

        // Admin Report - Organization Usage Report - Category Report Tab
        case 'Category':
            return (
                <Box p="16px">
                    <ReportSummary
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        title={t('Reports.report_category')}
                    />
                    <CategoryReports {...sharedProps} />
                </Box>
            );

        // Admin Report - Bussiness Owner Report
        case 'BusinessOwner':
            return <BusinessOwnerReports {...sharedProps} />;

        // Admin Report - Product Adoption Report
        case 'ProductAdoption':
            return <Adoption {...sharedProps} />;

        // Admin Report - Microsoft Office Overlap Report
        case 'MicrosoftOfficeOverlap':
            return <MicrosoftOfficeOverlapReports {...sharedProps} />;

        // Admin Report - Shadow IT Report
        case 'ShadowIT':
            return (
                <Box p="16px">
                    <ReportSummary
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        title={t('Reports.report_ShadowIT')}
                    />
                    <ShadowITReports {...sharedProps} />
                </Box>
            );

        // Admin Report - Application Map Report
        case 'Map':
            return (
                <Box p="16px">
                    <ReportSummary
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        title={t('Reports.report_applications')}
                    />
                    <Box mt="32px" width="100%">
                        <ApplicationMapReports {...sharedProps} />
                    </Box>
                </Box>
            );

        // Admin Report - User Application Inventory Report
        case 'UserApps':
            return (
                <Box p="16px">
                    <UserApps {...sharedProps} />
                </Box>
            );

        // Admin Report - Application Lifecycle Stage Breakdown Report - Application Lifecycle Stage Tab
        case 'ApplicationLifecycleStage':
            return (
                <Box p="16px">
                    <ReportSummary
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        title={t('Reports.report_status')}
                    />

                    <StatusReports {...sharedProps} />
                </Box>
            );

        // Admin Report - Contracts Report
        case 'Contracts':
            return <ContractsReport {...sharedProps} />;

        default:
            return null;
    }
};

export const ChooseReportComponent = ({
    renderingScheduledReport,
    reportData,
    reportSetup,
    tab
}) => {
    if (!reportData) {
        return null;
    }

    const reportType = getReportType({
        renderingScheduledReport,
        reportSetup,
        tab
    });

    const paperSx = isV2Report(reportType)
        ? {
              backgroundColor: 'background.reports',
              margin: '0 auto',
              maxWidth: '1280px',
              padding: '24px'
          }
        : { backgroundColor: 'background.reports', padding: 0 };

    return (
        <Paper
            elevation={0}
            variant="outlined"
            className="printMoveMarginLeft"
            sx={paperSx}
            data-trackid="chooseReportComponentByTab"
        >
            <Box
                className="pageBreak"
                data-trackid={`chooseReportComponent_${reportType}Report`}
                width="100%"
            >
                <RenderCurrentReport
                    renderingScheduledReport={renderingScheduledReport}
                    reportData={reportData}
                    reportSetup={reportSetup}
                    reportType={reportType}
                    tab={tab}
                />
            </Box>
        </Paper>
    );
};
